/**
 * "New contract" as a CRM dialog.
 *
 * Was a bordered box with shadcn fields inside `ModalShell`; it is the design's
 * own `Modal` now, with `crm-lab` labels over `crm-inp` pill fields, so it
 * matches every other dialog in the app.
 */
import { useEffect, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { DatePicker } from "@wa/ui";
import { useI18n, useT } from "../../../i18n/I18nProvider";
import {
  createContract, getContractTemplates, type Contract, type ContractTemplate,
} from "../../../lib/projects";
import { ClientPicker } from "../_shared/ClientPicker";
import { Btn, Modal, SelectInput } from "../../ui";
import { locField, toIso } from "./lib";

/** The "no template, write it yourself" option. Not an empty string: an empty
 *  <option> value and "the operator has not chosen yet" would look the same. */
const BLANK = "__blank__";

export function NewContractModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (c: Contract) => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const [templates, setTemplates] = useState<ContractTemplate[]>([]);
  const [templateKey, setTemplateKey] = useState(BLANK);
  const [clientName, setClientName] = useState("");
  const [clientId, setClientId] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [type, setType] = useState("");
  const [value, setValue] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [titleTouched, setTitleTouched] = useState(false);
  const [busy, setBusy] = useState(false);

  // A failure here leaves the picker at "blank contract", which is a usable
  // state - the dialog does not need templates to create anything.
  useEffect(() => {
    void getContractTemplates()
      .then((r) => setTemplates(r.templates))
      .catch(() => {});
  }, []);

  const tpl = templates.find((x) => x.key === templateKey);
  // The title writes itself from the template and the client until somebody
  // types over it; after that it is theirs and nothing overwrites it.
  const derivedTitle = tpl ? `${locField(tpl.name, locale)}${clientName ? ` - ${clientName}` : ""}` : "";
  const effectiveTitle = titleTouched ? title : derivedTitle || title;

  function pickTemplate(key: string) {
    setTemplateKey(key);
    const next = templates.find((x) => x.key === key);
    if (!next) return;
    setType(locField(next.type, locale));
    if (next.defaultValueCents != null) setValue(String(next.defaultValueCents / 100));
    if (!titleTouched) setTitle("");
  }

  const canSubmit = !!clientId && (templateKey !== BLANK || !!effectiveTitle.trim());

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || !clientId) return;
    setBusy(true);
    try {
      const c = await createContract({
        clientId,
        templateId: templateKey === BLANK ? undefined : templateKey,
        locale,
        title: effectiveTitle.trim() || undefined,
        type: type.trim() || undefined,
        valueCents: value ? Math.round(parseFloat(value) * 100) : undefined,
        startDate: start ? toIso(start) : undefined,
        endDate: end ? toIso(end) : undefined,
      });
      onCreated(c);
      onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={t("contracts.addTitle")} label={t("contracts.addTitle")} onClose={onClose} className="max-w-[520px]">
      <form onSubmit={submit} className="mt-1 flex flex-col gap-3">
        <div>
          <span className="crm-lab">{t("contracts.template")}</span>
          <SelectInput value={templateKey} onChange={pickTemplate} label={t("contracts.template")}>
            <option value={BLANK}>{t("contracts.blankTemplate")}</option>
            {templates.map((x) => (
              <option key={x.key} value={x.key}>
                {locField(x.name, locale)}
              </option>
            ))}
          </SelectInput>
          {tpl ? <p className="crm-fnote mt-1">{locField(tpl.description, locale)}</p> : null}
        </div>

        <div>
          <span className="crm-lab">{t("contracts.client")} *</span>
          <ClientPicker
            value={clientName}
            selectedId={clientId}
            onChange={(v) => {
              setClientName(v);
              setClientId(null);
            }}
            onPick={(id, nm) => {
              setClientId(id);
              setClientName(nm);
            }}
            placeholder={t("contracts.clientPlaceholder")}
            required
          />
        </div>

        <div>
          <span className="crm-lab">
            {t("contracts.titleField")} {templateKey === BLANK ? "*" : ""}
          </span>
          <label className="crm-inp">
            <input
              value={effectiveTitle}
              onChange={(e) => {
                setTitleTouched(true);
                setTitle(e.target.value);
              }}
              placeholder={t("contracts.titlePlaceholder")}
              required={templateKey === BLANK}
              autoComplete="off"
            />
          </label>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <span className="crm-lab">{t("contracts.type")}</span>
            <label className="crm-inp">
              <input
                value={type}
                onChange={(e) => setType(e.target.value)}
                placeholder={t("contracts.typePlaceholder")}
                autoComplete="off"
              />
            </label>
          </div>
          <div>
            <span className="crm-lab">{t("contracts.value")} (EUR)</span>
            <label className="crm-inp">
              <input
                type="number"
                step="0.01"
                min="0"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="0"
              />
            </label>
          </div>
          <div>
            <span className="crm-lab">{t("contracts.startDate")}</span>
            <DatePicker value={start} onChange={setStart} locale={locale} placeholder={t("contracts.startDate")} />
          </div>
          <div>
            <span className="crm-lab">{t("contracts.endDate")}</span>
            <DatePicker value={end} onChange={setEnd} locale={locale} placeholder={t("contracts.endDate")} />
          </div>
        </div>

        <div className="crm-m-foot">
          <Btn type="button" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Btn>
          <Btn type="submit" tone="brand" disabled={busy || !canSubmit}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
            {t("common.add")}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}
